import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useReverseGeocode from '@rsos/maps/interface/reverseGeocode/useReverseGeocode';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkInternationalSupport from '@rsos/utils/checkInternationalSupport';
import usePortalType from '@rsos/utils/usePortalType';
import useWhichReverseGeocodeService from '../../hooks/useWhichReverseGeocodeService';
import HoveredButton from '../HoveredButton';
import { NoAddress } from './NoAddress';
import { Loader } from './helpers';
import { handleCopy } from './helpers/handleCopy';
import {
  AddressBox,
  HomeStreet,
  CityRegion,
  TextWrapper,
  FadedTextWrapper,
  AddressContainer,
  AddressContainerWithCopy,
  InternationalAddress,
  ALISingleLineAddress,
} from './styles/locationAddress.styles';

const emptyAddressObject = {
  home: null,
  street: null,
  city: null,
  region: null,
  postal_code: null,
};

const LocationAddress = ({
  locationIndex,
  selectedCallerID,
  isCompact,
  isHoverable,
  isHistoricLocation,
  irpVersion,
  payloadID,
  requestID,
}) => {
  const { isPremiumUser, portalType } = usePortalType();

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasInternationalSupport = useSelector(state =>
    checkInternationalSupport({ psaps: state.psaps }),
  );

  const [isCopied, setIsCopied] = useState();

  const { locations, secondaryLocations } = useSelector(
    state => state.emergencyCalls.callQueue[selectedCallerID].core,
  );

  const getLocationIdx = locationIndex => {
    if (locationIndex !== undefined) {
      return locationIndex;
    }
    const locationsLength = locations?.length;
    const secondaryLocationsLength = secondaryLocations?.length;
    return locationsLength ? locationsLength - 1 : secondaryLocationsLength - 1;
  };

  const getLocation = locationIndex => {
    if (locationIndex !== undefined) {
      return locations[locationIndex] || secondaryLocations[locationIndex];
    } else {
      const locationsLength = locations?.length;
      const secondaryLocationsLength = secondaryLocations?.length;

      return (
        locations[locationsLength - 1] ||
        secondaryLocations[secondaryLocationsLength - 1]
      );
    }
  };

  const location = getLocation(locationIndex);

  const { rgAddress = '', address_single_line = '' } = location;

  const { home, street, city, region, postal_code } =
    location?.addressObject || emptyAddressObject;

  const noAddressFound = !rgAddress || (!home && !street && !city && !region);

  // When address is not found AND no address_single_line (address from ANI/ALI payload),
  // display the <NoAddress /> component.
  const showNoAddress = noAddressFound && !address_single_line;

  const HomeStreetAddress =
    home && street ? (
      <TextWrapper>{`${home} ${street}\u00A0`}</TextWrapper>
    ) : home && !street ? (
      <TextWrapper>{`${home}\u00A0`}</TextWrapper>
    ) : street && !home ? (
      hasEsriMapAccess ? (
        <>{`${street}\u00A0`}</>
      ) : (
        <>
          <FadedTextWrapper inline>(Bldg# N/A)</FadedTextWrapper>
          {`${street}\u00A0`}
        </>
      )
    ) : (
      <FadedTextWrapper>{`Street not available\u00A0`}</FadedTextWrapper>
    );

  const onClick = () => {
    const address = address_single_line
      ? address_single_line
      : hasInternationalSupport
      ? rgAddress
      : `${home ? `${home}\u00A0` : ''}${street ? `${street},\u00A0` : ''}${
          city ? `${city},\u00A0` : ''
        }${region} ${postal_code}`.trim();

    handleCopy({
      value: address,
      name: 'Location Address',
      irpVersion,
      payloadID,
      requestID,
      portalType,
      onStart: () => {
        setIsCopied(true);
      },
      onEnd: () => {
        setIsCopied(false);
      },
    });
  };

  const service = useWhichReverseGeocodeService();

  const { isReverseGeocoding } = useReverseGeocode({
    location,
    locationIndex: getLocationIdx(locationIndex),
    service,
  });

  return (
    <AddressBox {...{ isCompact }} data-name="location-address" responsive>
      {location?.uncertainty_radius > 100 ? (
        `Address not displayed due to large uncertainty radius`
      ) : isReverseGeocoding ? (
        <Loader />
      ) : showNoAddress ? (
        <NoAddress />
      ) : (
        (rgAddress || address_single_line) && (
          <AddressContainerWithCopy
            isHoverable={isHoverable}
            copied={isCopied}
            br={'5px'}
            align={'flex-start'}
          >
            <AddressContainer
              pr={2}
              br="5px"
              ellipsis
              flexWrap="wrap"
              minHeight="25px"
              align="flex-start"
              isHistoricLocation={isHistoricLocation}
            >
              {address_single_line ? (
                <ALISingleLineAddress
                  {...{ isCompact }}
                  fontWeight="regular"
                  isHistoricLocation={isHistoricLocation}
                >
                  {address_single_line}
                </ALISingleLineAddress>
              ) : hasInternationalSupport ? (
                <InternationalAddress
                  {...{ isCompact }}
                  fontWeight="regular"
                  isHistoricLocation={isHistoricLocation}
                >
                  {rgAddress}
                </InternationalAddress>
              ) : (
                <>
                  <HomeStreet
                    {...{ isCompact }}
                    isHistoricLocation={isHistoricLocation}
                    ellipsis
                    isHoverable={isHoverable}
                  >
                    {HomeStreetAddress}
                  </HomeStreet>
                  <CityRegion
                    {...{ isCompact }}
                    isHistoricLocation={isHistoricLocation}
                    ellipsis
                  >
                    {city}, {region} {postal_code}
                  </CityRegion>
                </>
              )}
            </AddressContainer>
            {isHoverable && !showNoAddress && (
              <HoveredButton
                onClick={onClick}
                isCopied={isCopied}
                withIcon
                absolute
              />
            )}
          </AddressContainerWithCopy>
        )
      )}
    </AddressBox>
  );
};

export default LocationAddress;
